import {
  ListTaxWithholdingTransactionsFilterParameterStatusEq as TaxWithholdingTransactionStatus,
  TaxWithholdingTransactionType
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { queriesTaxWithholdings } from "@query";
import { selectorGroupedTaxWithholdingTransactions } from "../selectors/selectorGroupedTaxWithholdingTransactions";

/**
 * Encapsulates the tax-withholding payment scheduling state derived from the
 * tax-withholding transaction feed: scheduled (future) and processing payments,
 * their amounts, and whether a tax payment is already scheduled/processing.
 *
 * Reads only the `Scheduled`/`Processing` rows via the list `status` filter
 * (WIN-15214) rather than walking the whole feed: those are the only statuses
 * the scheduling grouping cares about, so this reliably detects an existing
 * scheduled/processing payment — to avoid a duplicate the backend rejects with a
 * 409, and to know when to offer "Cancel scheduled payment" — without loading the
 * (potentially long) posted history. `useQueryAll` still drains any extra pages
 * so a member with many in-flight rows can't miss one.
 *
 * Shared by the Tax Withholdings dashboard toolbar (to hide the "Schedule tax
 * payment" action when a payment already exists) and the AvailableBalance card.
 */
export function useTaxPaymentScheduling() {
  const query =
    queriesTaxWithholdings.listTaxWithholdingTransactions.useQueryAll({
      filter: {
        status: {
          anyOf: new Set([
            TaxWithholdingTransactionStatus.SCHEDULED,
            TaxWithholdingTransactionStatus.PROCESSING
          ])
        }
      }
    });

  const grouped = useMemo(
    () => selectorGroupedTaxWithholdingTransactions(query.flatData ?? []),
    [query.flatData]
  );

  const scheduledPayments = grouped.scheduled;
  const processingPayments = grouped.processing;

  const scheduledPaymentAmount = useMemo(
    () => scheduledPayments.reduce((sum, t) => sum + Number(t.amount), 0),
    [scheduledPayments]
  );

  const processingPaymentAmount = useMemo(
    () => processingPayments.reduce((sum, t) => sum + Number(t.amount), 0),
    [processingPayments]
  );

  const hasScheduledPayment = scheduledPayments.length > 0;
  const hasProcessingPayment = processingPayments.length > 0;
  const hasScheduledTaxPayment = scheduledPayments.some(
    t => t.type === TaxWithholdingTransactionType.TAX_PAYMENT
  );
  const hasProcessingTaxPayment = processingPayments.some(
    t => t.type === TaxWithholdingTransactionType.TAX_PAYMENT
  );

  // True until the transaction feed has resolved for the first time. Consumers
  // use this to avoid showing a "Schedule tax payment" action before we know
  // whether one already exists (which would let the user submit a duplicate the
  // backend rejects).
  const isLoading = query.isInitialLoading;

  return {
    scheduledPayments,
    processingPayments,
    scheduledPaymentAmount,
    processingPaymentAmount,
    hasScheduledPayment,
    hasProcessingPayment,
    hasScheduledTaxPayment,
    hasProcessingTaxPayment,
    isLoading
  };
}
